/**
 * useAnalyticsReports - Report fetching and incremental caching for analytics
 *
 * Extracted from useAnalyticsData.js. Owns:
 * - The full report cache (allReports) with incremental time-range fetching:
 *   widening the time filter only fetches the gap older than what's cached
 * - Org-change detection that resets the cache
 * - Per-org analytics cutoff dates ("clean slate" performance metrics)
 *
 * @param {string} organization - Base organization
 * @param {Object} deps
 * @param {Array} deps.OrgFilterValue - Selected orgs from the org filter
 * @param {number} deps.activeFilter - Active time filter index
 * @param {Function} deps.handleError - Error handler from useErrorState
 */

import { useState, useEffect, useCallback, useRef } from 'react';

import Util from '../util/util.js';
import { getStartAndEndTimes } from '../util/analytics/queryUtils';
import { TIME_FILTER_CONFIGS } from '../util/analytics/constants';
import { t } from '../i18n';

const { DateTime } = require('luxon');

// Page size for paginated requests (stay safely under 6MB API Gateway limit)
const PAGE_SIZE = 100;

// All Time filter index - fetches from epoch
const ALL_TIME_FILTER_INDEX = 6;

/**
 * Calculate the start time for a given filter index
 * Returns milliseconds timestamp, or 0 for "All Time"
 */
export function getFilterStartTime(filterIndex) {
  if (filterIndex === ALL_TIME_FILTER_INDEX) {
    return 0; // All time
  }
  const config = TIME_FILTER_CONFIGS[filterIndex];
  if (!config) return 0;
  const [queryMode, timeAmount, timeStride] = config;
  const [startTime] = getStartAndEndTimes(queryMode, timeAmount * timeStride);
  // Handle both Luxon DateTime objects and raw timestamps (for test mocks)
  return typeof startTime === 'number' ? startTime : startTime.toMillis();
}

/**
 * Fetch all pages from a paginated endpoint
 * @param {string} apiName - API name (e.g., 'Resty')
 * @param {string} path - API path
 * @param {Object} baseParams - Base query parameters
 * @returns {Promise<Object>} Object with items array and optional metadata (cutoffDates)
 */
async function fetchAllPages(apiName, path, baseParams) {
  let allItems = [];
  let cursor = null;
  let hasMore = true;
  let cutoffDates = {};

  while (hasMore) {
    const queryParams = {
      ...baseParams,
      limit: PAGE_SIZE,
      ...(cursor && { cursor }),
    };

    const data = await Util.get(apiName, path, { queryParams });

    if (data.items && data.items.length > 0) {
      allItems = allItems.concat(data.items);
    }

    // Merge cutoffDates from each page (different orgs may be processed in different pages)
    if (data.cutoffDates) {
      cutoffDates = { ...cutoffDates, ...data.cutoffDates };
    }

    // Check pagination info
    if (data.pagination) {
      hasMore = data.pagination.hasMore;
      cursor = data.pagination.nextCursor;
    } else {
      // Non-paginated response (legacy) - we have all items
      hasMore = false;
    }
  }

  return { items: allItems, cutoffDates };
}

/**
 * Fetch analytics cutoff date for a single organization (fallback)
 */
const fetchAnalyticsCutoffFallback = async (org) => {
  try {
    const response = await Util.get('Resty', '/items/organization/analytics-cutoff', {
      queryParams: { organization: org },
    });
    return response?.data?.analytics_cutoff_date || null;
  } catch (err) {
    console.log('Failed to fetch analytics cutoff:', err);
    return null;
  }
};

export default function useAnalyticsReports(organization, { OrgFilterValue, activeFilter, handleError }) {
  // Report data state
  const [allReports, setAllReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(true);

  // Incremental fetch tracking - oldest timestamp we've loaded
  // null means we haven't fetched yet, 0 means we have all data
  const [oldestFetchedTime, setOldestFetchedTime] = useState(null);

  // Track which orgs we've fetched for (to reset cache on org change)
  const [fetchedOrgsKey, setFetchedOrgsKey] = useState('');

  // Analytics cutoff dates state (per-org map for "clean slate" performance metrics)
  const [analyticsCutoffDates, setAnalyticsCutoffDates] = useState({});

  /**
   * Fetch reports from API with incremental loading
   * Only fetches data older than what we've already cached
   */
  const fetchReports = useCallback(async (forceRefresh = false) => {
    const orgList = OrgFilterValue != null && OrgFilterValue.length > 0 ? OrgFilterValue : [organization];
    const orgsKey = orgList.sort().join(',');
    const endTime = DateTime.now().toMillis();

    // Calculate required start time based on active filter
    const requiredStartTime = getFilterStartTime(activeFilter);

    // Check if orgs changed - if so, reset cache
    const orgsChanged = orgsKey !== fetchedOrgsKey;
    if (orgsChanged) {
      setAllReports([]);
      setOldestFetchedTime(null);
      setFetchedOrgsKey(orgsKey);
    }

    // Determine what time range we need to fetch
    const currentOldest = orgsChanged ? null : oldestFetchedTime;

    // Skip fetch if we already have all required data
    if (!forceRefresh && currentOldest !== null && currentOldest <= requiredStartTime) {
      return; // Already have enough data cached
    }

    setReportsLoading(true);

    try {
      // Calculate the time range to fetch
      // If we have cached data, only fetch the gap (from requiredStartTime to currentOldest)
      // Otherwise, fetch from requiredStartTime to now
      let fetchStartTime = requiredStartTime;
      let fetchEndTime = endTime;

      if (currentOldest !== null && currentOldest > requiredStartTime) {
        // We have some cached data but need older data
        // Fetch from requiredStartTime up to (but not including) what we have
        fetchEndTime = currentOldest - 1;
      }

      const queryParams = {
        startTime: fetchStartTime,
        endTime: fetchEndTime,
        organization,
        organizations: orgList.toString(),
        statsOnly: true,
      };

      // Use paginated fetch to handle large datasets safely (avoids 6MB API Gateway limit)
      const { items, cutoffDates } = await fetchAllPages('Resty', '/items/queryTimeRange', queryParams);

      // Normalize legacy reports missing the status field - use isClosed as fallback
      // Only apply when status is absent; if status exists, trust it over isClosed
      const normalizedItems = (items || []).map((report) => {
        if (!report.status && (report.isClosed || report.closed)) {
          return { ...report, status: 'Closed' };
        }
        return report;
      });

      // Merge new items with existing cache
      if (currentOldest !== null) {
        // Incremental fetch - prepend older items to existing cache
        // Time ranges don't overlap, so no deduplication needed
        if (normalizedItems.length > 0) {
          setAllReports(prev => [...normalizedItems, ...prev]);
        }
        // If items is empty, keep existing data (no older data exists)
      } else {
        // First fetch or org changed - replace entirely
        setAllReports(normalizedItems);
      }

      // Update oldest fetched time
      const newOldest = requiredStartTime === 0 ? 0 : Math.min(fetchStartTime, currentOldest ?? Infinity);
      setOldestFetchedTime(newOldest);

      // Use cutoffDates from response if available
      if (cutoffDates && Object.keys(cutoffDates).length > 0) {
        setAnalyticsCutoffDates(prev => ({ ...prev, ...cutoffDates }));
      } else if (orgsChanged || Object.keys(analyticsCutoffDates).length === 0) {
        // Fallback: fetch cutoff dates separately for each viewed org (only on first load or org change)
        const fallbackCutoffs = {};
        for (const org of orgList) {
          fallbackCutoffs[org] = await fetchAnalyticsCutoffFallback(org);
        }
        setAnalyticsCutoffDates(fallbackCutoffs);
      }
    } catch (err) {
      handleError(err, { userMessage: t('analytics:loadFailed') });
    }

    setReportsLoading(false);
  }, [organization, OrgFilterValue, activeFilter, oldestFetchedTime, fetchedOrgsKey, analyticsCutoffDates, handleError]);

  // Refs to hold latest values for the filter change effect
  // This avoids stale closure issues
  const fetchReportsRef = useRef(fetchReports);
  const oldestFetchedTimeRef = useRef(oldestFetchedTime);

  // Keep refs in sync
  useEffect(() => {
    fetchReportsRef.current = fetchReports;
  }, [fetchReports]);

  useEffect(() => {
    oldestFetchedTimeRef.current = oldestFetchedTime;
  }, [oldestFetchedTime]);

  // Fetch more data when filter changes to require older data
  useEffect(() => {
    // Only trigger if we have already fetched some data (oldestFetchedTime is set)
    // This prevents double-fetching on initial mount
    const currentOldest = oldestFetchedTimeRef.current;
    if (currentOldest !== null) {
      const requiredStartTime = getFilterStartTime(activeFilter);
      // If the filter now requires data older than what we have, fetch more
      if (requiredStartTime < currentOldest) {
        fetchReportsRef.current();
      }
    }
  }, [activeFilter]);

  return {
    allReports,
    reportsLoading,
    analyticsCutoffDates,
    fetchReports,
  };
}
